import React, { MouseEventHandler } from 'react';
import styled, { css } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';

import {
  modalTypeSelector,
  modalPropsSelector,
  isSdkSelector,
  isMobileSelector
} from 'redux/selectors/global';
import { hideModal } from 'redux/actions/global';
import ModalBase, { IModalProps as IBaseModalProps } from 'components/core/Modal/Modal';
import MODAL_TYPES from 'components/containers/Modal/Modals/types';
import CameraModal from 'components/containers/Modal/Modals/Camera';
import UploadModal from 'components/containers/Modal/Modals/Upload';
import ResetPINModal from 'components/containers/Modal/Modals/ResetPIN';
import ShareDemoModal from 'components/containers/Modal/Modals/Demo/ShareDemo';
import DemoAuthPayment from 'components/containers/Modal/Modals/Demo/DemoAuthPayment';
import DemoCreatePaymentModal from 'components/containers/Modal/Modals/Demo/DemoCreatePayment';
import DemoDigitalWalletModal from 'components/containers/Modal/Modals/Demo/DemoDigitalWallet';
import { getThemeTypography, Typographies } from 'models/uiElements';

type IModalProps = IBaseModalProps;

const Modal = ({ className }: IModalProps) => {
  const dispatch = useDispatch();

  const modalType = useSelector(modalTypeSelector);
  const modalProps = useSelector(modalPropsSelector);
  const isSdk = useSelector(isSdkSelector);
  const isMobile = useSelector(isMobileSelector);

  const showModal = !!modalType;
  const { generalProps, contentProps } = modalProps ?? {};

  const {
    name,
    title,
    subTitle,
    onCloseClick,
    primaryButtonProps,
    secondaryButtonProps,
    themeFamily,
    staticSize,
    shouldDisableCloseOption
  } = generalProps ?? {};

  const { settings } = contentProps ?? {};

  const closeModal = (): void => {
    if (shouldDisableCloseOption) return;
    dispatch(hideModal());
    if (onCloseClick) onCloseClick();
  };

  const handleBackdropClick: MouseEventHandler<HTMLDivElement> = (): void => {
    if (shouldDisableCloseOption) return;
    if (!disabledBackdrop) closeModal();
  };

  const renderModal = {
    [MODAL_TYPES.UPLOAD]: () => <UploadModal {...contentProps} />,
    [MODAL_TYPES.CAMERA]: () => <CameraModal {...contentProps} />,
    [MODAL_TYPES.RESET_PIN]: () => <ResetPINModal {...contentProps} />,
    [MODAL_TYPES.DEMO_CREATE_PAYMENT]: () => <DemoCreatePaymentModal {...contentProps} />,
    [MODAL_TYPES.SHARE_DEMO_MODAL]: () => <ShareDemoModal {...contentProps} />,
    [MODAL_TYPES.DEMO_DIGITAL_WALLET]: () => <DemoDigitalWalletModal {...contentProps} />,
    [MODAL_TYPES.DEMO_AUTH_PAYMENT]: () => <DemoAuthPayment {...contentProps} />
  };

  const { backdrop = true, disabledBackdrop = false } = settings ?? {};
  const renderModalFunc = renderModal[modalType];

  const isDemoModal = [
    MODAL_TYPES?.DEMO_CREATE_PAYMENT,
    MODAL_TYPES?.SHARE_DEMO_MODAL,
    MODAL_TYPES?.DEMO_DIGITAL_WALLET
  ].includes(modalType);

  return showModal ? (
    <Wrapper>
      <ModalWrapper>
        <StyledModalBase
          modalType={modalType}
          isDesktop={!isMobile && !isSdk}
          className={className}
          name={name}
          title={title}
          subTitle={subTitle}
          onCloseClick={closeModal}
          primaryButtonProps={primaryButtonProps}
          secondaryButtonProps={secondaryButtonProps}
          themeFamily={themeFamily}
          staticSize={staticSize}
          isDemoModal={isDemoModal}
        >
          {renderModalFunc ? renderModalFunc() : null}
        </StyledModalBase>
      </ModalWrapper>
      {backdrop && <Backdrop onClick={handleBackdropClick} />}
    </Wrapper>
  ) : null;
};

export default Modal;

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: 160;
`;

const StyledModalBase = styled(ModalBase)<{
  modalType: string;
  isDesktop: boolean;
  isDemoModal: boolean;
}>`
  color: ${({ theme }) => theme.colors.primary};

  ${({ modalType, isDesktop, isDemoModal, theme }) => {
    const { isMobile, isSdk } = theme.view;

    if (modalType === MODAL_TYPES.UPLOAD) {
      return css`
        width: ${!isMobile ? '42rem' : '100%'};
        height: 33rem;
      `;
    }

    if (modalType === MODAL_TYPES.RESET_PIN) {
      if (isMobile) {
        return css`
          width: 100%;
          text-align: center;
        `;
      }

      return css`
        text-align: center;
      `;
    }

    if (modalType === MODAL_TYPES.CAMERA) {
      if (isDesktop) {
        return css`
          max-height: 66rem;
          width: 72.8rem;
          height: 66.5rem;
        `;
      }

      if (isSdk) {
        return css`
          width: 65rem;
          height: 56rem;
          max-height: 56rem;
        `;
      }

      return css`
        width: 100%;
        height: 51.6rem;
      `;
    }

    if (modalType === MODAL_TYPES.CAMERA_PERMISSION_EXPLANATION) {
      if (isMobile) {
        return css`
          width: 100%;
        `;
      }
    }

    if (isDemoModal) {
      return css`
        &[data-testid$='modal-container'] {
          z-index: 20;
          width: ${({ theme }) => (theme.view.isMobile ? '32.5rem' : '67rem')};

          padding-bottom: 2rem;
        }

        button[data-testid='primary-button'] {
          ${getThemeTypography(Typographies.H7_HEADING)};
          width: 17rem;
          height: 5rem;
        }
      `;
    }

    if (modalType === MODAL_TYPES?.DEMO_AUTH_PAYMENT) {
      return css`
        &[data-testid$='modal-container'] {
          z-index: 20;
          min-width: 33rem;
          max-height: 65rem;
          width: ${({ theme }) => (theme.view.isMobile ? '100%' : '73rem')};
          height: ${({ theme }) => (theme.view.isMobile ? '60rem' : '65rem')};
          padding-bottom: 2rem;
        }

        button[data-testid='primary-button'] {
          ${getThemeTypography(Typographies.H7_HEADING)};
          width: 17rem;
          height: 5rem;
        }
      `;
    }
  }};
`;

const ModalWrapper = styled.div`
  position: relative;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;

  ${({ theme }) => {
    const { isMobile } = theme.view;

    if (isMobile) {
      return css`
        position: fixed;
        > div {
          max-width: 90vw;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;

          > div {
            background-color: unset;
          }
        }
      `;
    }
  }}
`;

const Backdrop = styled.div`
  width: 100%;
  height: 100%;
  background: rgba(26, 26, 26, 0.8);
`;
